import {
  evaluateSelfServicePolicy,
  type SelfServiceEligibility,
  type SelfServiceResendPolicy,
} from './review-request-policy.ts';

export interface ActivityFilterRequest {
  q?: string;
  status?: string;
  dateRange?: string;
  role?: string;
}

export interface CommunicationActivityRow {
  id: string;
  organization_id?: string | null;
  created_at?: string | null;
  delivered_at?: string | null;
  subject?: string | null;
  status?: string | null;
  email_address?: string | null;
  content?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface CdaQueueActivityRow {
  id: string;
  encompass_loan_id?: string | null;
  status?: string | null;
  processor_email?: string | null;
  resend_id?: string | null;
  email_sent_at?: string | null;
  loan_number?: string | null;
}

export const CDA_ORDER_NOTIFICATION_TYPE = 'appraisal_cda_order_required';
export const ACTIVITY_SENT_STATUSES = ['sent'];

// ---------------------------------------------------------------------------
// Delivery status (Resend/Svix ledger `email_events`)
// ---------------------------------------------------------------------------

export type DeliveryState = 'delivered' | 'bounced' | 'complained' | 'pending' | 'unknown';

export interface DeliveryStatus {
  state: DeliveryState;
  /** occurred_at of the event that decided the state; null for pending/unknown. */
  at: string | null;
}

export interface EmailEventRow {
  resend_id: string;
  event_type: string;
  occurred_at: string;
  recipient_email?: string | null;
}

/** With no delivery event yet, a send younger than this is still "awaiting confirmation". */
export const DELIVERY_PENDING_WINDOW_MS = 24 * 60 * 60 * 1000;

function normalizeEmailAddress(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

/**
 * Delivery state for ONE attempt. Resend emits a separate event stream per envelope
 * recipient (the copied loan officer has their own delivered/bounced events), so only
 * events whose recipient matches the attempt's primary TO address count. Precedence over
 * the whole stream: complained > bounced > delivered (opened/clicked imply delivered).
 */
export function deliveryStatusFor(
  attempt: { resendId: string | null; toEmails: string[]; sentAt: string | null },
  events: readonly EmailEventRow[],
  now: number = Date.now(),
): DeliveryStatus {
  const recipient = normalizeEmailAddress(attempt.toEmails[0]);
  const relevant = attempt.resendId && recipient
    ? events.filter((event) =>
      event.resend_id === attempt.resendId
      && normalizeEmailAddress(event.recipient_email) === recipient)
    : [];

  const earliest = (types: string[]): EmailEventRow | null => {
    let found: EmailEventRow | null = null;
    for (const event of relevant) {
      if (!types.includes(event.event_type)) continue;
      if (!found || Date.parse(event.occurred_at) < Date.parse(found.occurred_at)) {
        found = event;
      }
    }
    return found;
  };

  const complained = earliest(['email.complained']);
  if (complained) return { state: 'complained', at: complained.occurred_at };
  const bounced = earliest(['email.bounced']);
  if (bounced) return { state: 'bounced', at: bounced.occurred_at };
  const delivered = earliest(['email.delivered', 'email.opened', 'email.clicked']);
  if (delivered) return { state: 'delivered', at: delivered.occurred_at };

  const sentMs = attempt.sentAt ? Date.parse(attempt.sentAt) : Number.NaN;
  const young = !Number.isNaN(sentMs) && now - sentMs < DELIVERY_PENDING_WINDOW_MS;
  return { state: young ? 'pending' : 'unknown', at: null };
}

export function deliveryLabel(state: DeliveryState | null | undefined): string {
  switch (state) {
    case 'delivered': return 'Delivered';
    case 'bounced': return 'Bounced';
    case 'complained': return 'Marked as spam';
    case 'pending': return 'Sent, awaiting confirmation';
    case 'unknown': return 'Sent, no delivery record';
    default: return '';
  }
}

/**
 * Tenant bound for the admin scope: drop every attempt positively known to belong to ANOTHER
 * organization, through its own `organization_id` or through its loan's organization.
 * Rows with no tenant signal at all stay visible: measured 2026-09-02, only 24 of 9,485 sent
 * emails carry an organization id and 6,979 resolve to no loan or sender either, so a strict
 * bound would blank the admin view. Attribution backfill is the follow-up that lets this
 * become strict.
 */
export function filterAttemptsToOrganization<T extends { organizationId: string | null; context: { encompass_loan_id?: string | null } }>(
  attempts: readonly T[],
  organizationId: string,
  loanOrganizationById: ReadonlyMap<string, string>,
): T[] {
  return attempts.filter((attempt) => {
    if (attempt.organizationId) return attempt.organizationId === organizationId;
    const loanId = attempt.context.encompass_loan_id;
    if (!loanId) return true;
    const loanOrganization = loanOrganizationById.get(loanId);
    return loanOrganization === undefined || loanOrganization === organizationId;
  });
}

/** review_request_sends columns the eligibility attachment reads. */
export interface ReviewLedgerRow {
  encompass_loan_id: string;
  recipient_email: string;
  recipient_role: string;
  status: string;
  attempt_count: number | null;
  last_attempt_at?: string | null;
}

function ledgerKey(loanId: string | null | undefined, email: string | null | undefined, role: string | null | undefined): string {
  return `${loanId ?? ''}|${normalizeEmailAddress(email)}|${roleKey(role)}`;
}

/**
 * Attach the loan-team resend eligibility the server will enforce, computed from the same
 * ledger rows the sender's claim step reads, so the page never guesses from what it can see.
 * Rows of types that cannot be resent are left untouched (`resend` stays null).
 */
export function attachResendEligibility<T extends { toEmails: string[]; notificationType: string; context: { encompass_loan_id?: string | null; recipient_role?: string | null }; resend: SelfServiceEligibility | null }>(
  attempts: readonly T[],
  ledgerRows: readonly ReviewLedgerRow[],
  resendableTypes: ReadonlySet<string>,
  policy: SelfServiceResendPolicy,
  now: number = Date.now(),
): T[] {
  const ledger = new Map<string, ReviewLedgerRow>();
  for (const row of ledgerRows) {
    ledger.set(ledgerKey(row.encompass_loan_id, row.recipient_email, row.recipient_role), row);
  }
  return attempts.map((attempt) => {
    if (!resendableTypes.has(attempt.notificationType)) return attempt;
    const row = ledger.get(ledgerKey(attempt.context.encompass_loan_id, attempt.toEmails[0], attempt.context.recipient_role)) ?? null;
    return { ...attempt, resend: evaluateSelfServicePolicy(row, policy, now) };
  });
}

export function boolFromMetadata(value: unknown): boolean {
  return value === true || value === 'true';
}

export function arrayFromMetadata(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry)).filter(Boolean);
  }
  if (typeof value === 'string' && value.trim()) {
    return value.split(',').map((entry) => entry.trim()).filter(Boolean);
  }
  return [];
}

function stringFromMetadata(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  if (typeof value === 'number') {
    return String(value);
  }
  if (Array.isArray(value)) {
    const values = value.map((entry) => String(entry).trim()).filter(Boolean);
    return values.length > 0 ? values.join(', ') : null;
  }
  return null;
}

function objectFromMetadata(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function loanNumberFromSubject(subject: string | null | undefined): string | null {
  const match = subject?.match(/\b(?:TEST-DRAFT-)?LW\d+\b/i);
  return match ? match[0] : null;
}

function loanNumbersFromCollection(value: unknown): string | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const numbers = value
    .map((entry) => {
      const item = objectFromMetadata(entry);
      return stringFromMetadata(item.loan_number)
        || stringFromMetadata(item.loanNumber)
        || stringFromMetadata(item.trans_details_loan_number);
    })
    .filter((loanNumber): loanNumber is string => Boolean(loanNumber));
  const unique = Array.from(new Set(numbers));
  return unique.length > 0 ? unique.join(', ') : null;
}

function borrowerNamesFromCollection(value: unknown): string | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const names = value
    .map((entry) => {
      const item = objectFromMetadata(entry);
      return stringFromMetadata(item.borrower_name) || stringFromMetadata(item.borrowerName);
    })
    .filter((borrowerName): borrowerName is string => Boolean(borrowerName));
  const unique = Array.from(new Set(names));
  return unique.length > 0 ? unique.join(', ') : null;
}

export function sentAt(row: { delivered_at?: string | null; created_at?: string | null }) {
  return row.delivered_at || row.created_at || null;
}

export function isWithinSentWindow(
  row: { delivered_at?: string | null; created_at?: string | null },
  cutoff: string | null,
): boolean {
  if (!cutoff) {
    return true;
  }
  const timestamp = sentAt(row);
  return Boolean(timestamp && Date.parse(timestamp) >= Date.parse(cutoff));
}

export function sentTimestampCutoffFilter(cutoff: string): string {
  return `delivered_at.gte.${cutoff},and(delivered_at.is.null,created_at.gte.${cutoff})`;
}

export function curatedContext(metadata: Record<string, unknown>, subject?: string | null) {
  const data = objectFromMetadata(metadata.data);
  const loanNumber = stringFromMetadata(metadata.loan_number)
    || stringFromMetadata(metadata.loanNumber)
    || stringFromMetadata(metadata.loan_numbers)
    || stringFromMetadata(data.loan_number)
    || stringFromMetadata(data.loanNumber)
    || stringFromMetadata(data.loan_numbers)
    || loanNumbersFromCollection(data.loans)
    || loanNumberFromSubject(subject);
  const recipientRole = stringFromMetadata(metadata.recipient_role)
    || stringFromMetadata(metadata.assignee_role)
    || stringFromMetadata(data.recipient_role)
    || stringFromMetadata(data.recipientRole)
    || stringFromMetadata(data.assignee_role)
    || stringFromMetadata(data.assigneeRole);
  return {
    loan_number: loanNumber,
    encompass_loan_id: stringFromMetadata(metadata.encompass_loan_id)
      || stringFromMetadata(metadata.loan_id)
      || stringFromMetadata(metadata.loanId)
      || stringFromMetadata(data.encompass_loan_id)
      || stringFromMetadata(data.encompassLoanId)
      || stringFromMetadata(data.loan_id)
      || stringFromMetadata(data.loanId),
    borrower_name: stringFromMetadata(metadata.borrower_name)
      || stringFromMetadata(metadata.borrowerName)
      || stringFromMetadata(data.borrower_name)
      || stringFromMetadata(data.borrowerName)
      || borrowerNamesFromCollection(data.loans),
    recipient_role: recipientRole,
    channel: stringFromMetadata(metadata.channel) || stringFromMetadata(data.channel),
    is_test: boolFromMetadata(metadata.is_test),
    // communications.id of the email this row repeats (admin resend from Activity).
    resend_of: stringFromMetadata(metadata.resend_of),
  };
}

export function roleKey(role: string | null | undefined): string {
  const normalized = (role || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (normalized === 'lo2') return 'loan_officer_2';
  if (normalized === 'loa') return 'loan_officer_assistant';
  return normalized;
}

function statusKey(status: string | null | undefined): string {
  const normalized = (status || 'sent').toLowerCase();
  if (normalized === 'sent') return 'sent';
  if (normalized === 'delivered') return 'delivered';
  return normalized;
}

function statusLabel(status: string | null | undefined): string {
  const key = statusKey(status);
  if (key === 'sent') return 'Sent';
  if (key === 'delivered') return 'Delivered';
  return key ? key.charAt(0).toUpperCase() + key.slice(1) : 'Sent';
}

export function statusValuesForFilter(status: string | null | undefined): string[] {
  switch ((status || 'all').toLowerCase()) {
    case 'all':
    case 'sent':
    case 'delivered':
      return ACTIVITY_SENT_STATUSES;
    default:
      return [];
  }
}

export function shouldLoadCdaQueueForStatus(status: string | null | undefined): boolean {
  return statusValuesForFilter(status).includes('sent');
}

export function dateCutoff(dateRange: string | null | undefined): string | null {
  const daysByRange: Record<string, number> = {
    '1': 1,
    '7': 7,
    '30': 30,
    '90': 90,
    '365': 365,
  };
  const days = daysByRange[String(dateRange || '30')];
  if (!days) return null;
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
}

export function toAttempt(row: CommunicationActivityRow) {
  const metadata = (row.metadata || {}) as Record<string, unknown>;
  return {
    id: row.id,
    organizationId: row.organization_id ?? null,
    notificationType: String(metadata.notification_type || ''),
    subject: row.subject ?? null,
    status: row.status ?? null,
    sentAt: sentAt(row),
    toEmails: arrayFromMetadata(metadata.to_emails || metadata.recipient_emails || metadata.to || metadata.toRecipients || row.email_address),
    ccEmails: arrayFromMetadata(metadata.cc_emails || metadata.cc || metadata.ccRecipients || metadata.cc_recipients),
    resendId: typeof metadata.resend_id === 'string' ? metadata.resend_id : null,
    hasStoredHtml: Boolean(row.content),
    delivery: null as DeliveryStatus | null,
    resend: null as SelfServiceEligibility | null,
    context: curatedContext(metadata, row.subject),
  };
}

export type ActivityAttempt = ReturnType<typeof toAttempt>;

/** Attach a delivery status to every attempt from the events fetched for their resend ids. */
export function attachDelivery(
  attempts: readonly ActivityAttempt[],
  events: readonly EmailEventRow[],
  now: number = Date.now(),
): ActivityAttempt[] {
  return attempts.map((attempt) => ({ ...attempt, delivery: deliveryStatusFor(attempt, events, now) }));
}

export function queueToAttempt(row: CdaQueueActivityRow) {
  return {
    id: row.id,
    organizationId: null as string | null,
    notificationType: CDA_ORDER_NOTIFICATION_TYPE,
    subject: 'CDA Order Required',
    status: row.status ?? 'sent',
    sentAt: row.email_sent_at ?? null,
    toEmails: arrayFromMetadata(row.processor_email),
    ccEmails: [],
    resendId: row.resend_id ?? null,
    hasStoredHtml: Boolean(row.resend_id),
    delivery: null as DeliveryStatus | null,
    resend: null as SelfServiceEligibility | null,
    context: {
      loan_number: row.loan_number ?? null,
      encompass_loan_id: row.encompass_loan_id ?? null,
      borrower_name: null,
      recipient_role: 'processor',
      channel: 'resend_appraisal_cda',
      is_test: false,
      resend_of: null,
    },
  };
}

function attemptSearchText(attempt: ActivityAttempt): string {
  return [
    ...attempt.toEmails,
    ...attempt.ccEmails,
    attempt.context.loan_number,
    attempt.context.encompass_loan_id,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export function matchesClientFilters(
  attempt: ActivityAttempt,
  body: ActivityFilterRequest,
  includeTests: boolean,
): boolean {
  if (!includeTests && attempt.context.is_test) {
    return false;
  }
  const q = (body.q || '').trim().toLowerCase();
  if (q && !attemptSearchText(attempt).includes(q)) {
    return false;
  }
  const role = roleKey(body.role);
  if (role && role !== 'all' && roleKey(attempt.context.recipient_role) !== role) {
    return false;
  }
  return true;
}

export function buildStats(attempts: ActivityAttempt[]) {
  const stats = { total: attempts.length, delivered: 0, opened: 0, bounced: 0, failed: 0, openRate: null as number | null };
  for (const attempt of attempts) {
    // Provider-confirmed delivery beats the row status; a bare row status of 'delivered'
    // (no ledger data) keeps the legacy behavior.
    const state = attempt.delivery?.state;
    if (state === 'delivered' || (!state && statusKey(attempt.status) === 'delivered')) {
      stats.delivered += 1;
    } else if (state === 'bounced') {
      stats.bounced += 1;
    } else if (state === 'complained') {
      stats.failed += 1;
    }
  }
  return stats;
}

function csvValue(value: unknown): string {
  const text = value === null || value === undefined ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

export function buildCsv(attempts: ActivityAttempt[]): string {
  const lines = ['Timestamp,Recipient,Loan,Role,Status,Delivery'];
  for (const attempt of attempts) {
    lines.push([
      csvValue(attempt.sentAt || ''),
      csvValue(attempt.toEmails.join('; ') || 'Unknown'),
      csvValue(attempt.context.loan_number || attempt.context.encompass_loan_id || ''),
      csvValue(attempt.context.recipient_role || ''),
      csvValue(statusLabel(attempt.status)),
      csvValue(deliveryLabel(attempt.delivery?.state)),
    ].join(','));
  }
  return lines.join('\n');
}
